import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Form, notification, Spin } from 'antd'

import { Drawer, DrawerFooter } from '@/components/ui/drawer'
import { Button } from '@/components/ui/button'
import { CounterForm } from '@/components/form/CounterForm'
import type { CounterFormValues } from '@/components/form/CounterForm'
import { createCounter, updateCounter, getCounterById } from '@/api/counters.api'
import type { CounterWriteInput } from '@/api/counters.api'
import { currentUserAtom } from '@/store/auth.atom'
import { useAtomValue } from 'jotai'

interface CounterFormDrawerProps {
    isOpen: boolean
    mode: 'create' | 'update'
    counterId: string | null
    onClose: () => void
}

export function CounterFormDrawer({ isOpen, mode, counterId, onClose }: CounterFormDrawerProps) {
    const [form] = Form.useForm<CounterFormValues>()
    const queryClient = useQueryClient()
    const currentUser = useAtomValue(currentUserAtom)
    const showUnitColumn = currentUser?.userType === 1

    const { data: counter, isLoading: isFetchingCounter } = useQuery({
        queryKey: ['counter-detail', counterId],
        queryFn: ({ signal }) => counterId ? getCounterById(counterId, signal) : Promise.resolve(null),
        enabled: isOpen && mode === 'update' && !!counterId,
        refetchOnMount: 'always',
        staleTime: 0,
        gcTime: 0,
    })

    useEffect(() => {
        if (isOpen) {
            if (mode === 'update' && counter) {
                form.setFieldsValue({
                    id: counter.id,
                    deskNumber: counter.deskNumber,
                    name: counter.name,
                    unitId: counter.unitId || '',
                    unitName: counter.unitName,
                    diagramId: counter.diagramId || '',
                    diagramName: counter.diagramName,
                })
            } else if (mode === 'create') {
                form.resetFields()
            }
        } else {
            form.resetFields()
        }
    }, [isOpen, mode, counter, form])

    const createMutation = useMutation({
        mutationFn: createCounter,
        onSuccess: (response: any) => {
            notification.success({ message: response.responseMessage || 'Thêm mới quầy thành công!' })
            queryClient.invalidateQueries({ queryKey: ['counters'] })
            queryClient.invalidateQueries({ queryKey: ['remote-infinite-select', 'counter-desk'] })
            onClose()
        },
    })

    const updateMutation = useMutation({
        mutationFn: (values: CounterWriteInput) => {
            if (!counterId) throw new Error('Missing counter ID')
            return updateCounter(counterId, values)
        },
        onSuccess: (response: any) => {
            notification.success({ message: response.responseMessage || 'Cập nhật quầy thành công!' })
            queryClient.invalidateQueries({ queryKey: ['counters'] })
            queryClient.invalidateQueries({ queryKey: ['remote-infinite-select', 'counter-desk'] })
            onClose()
        },
    })

    const isSubmitting = createMutation.isPending || updateMutation.isPending
    const isDrawerDisabled = isSubmitting || isFetchingCounter

    const handleSubmit = (values: CounterFormValues) => {
        const payload: CounterWriteInput = {
            deskNumber: Number(values.deskNumber),
            name: values.name,
            unitId: showUnitColumn ? values.unitId : currentUser?.unitId,
            diagramId: values.diagramId,
            ...(mode === 'update' ? { status: counter?.status ?? 1 } : {}),
        }

        if (mode === 'create') {
            createMutation.mutate(payload)
        } else {
            updateMutation.mutate(payload)
        }
    }

    return (
        <Drawer
            open={isOpen}
            onClose={onClose}
            title={mode === 'create' ? 'THÊM MỚI QUẦY' : 'CẬP NHẬT QUẦY'}
            styles={{ body: { overflow: 'auto' } }}
            footer={
                <DrawerFooter>
                    <Button variant="outline" size="md" onClick={onClose} disabled={isDrawerDisabled} className="min-w-[160px] h-10">Hủy</Button>
                    <Button variant="primary" size="md" onClick={() => form.submit()} disabled={isDrawerDisabled} className="min-w-[160px] h-10">
                        {isSubmitting ? 'Đang lưu...' : 'Lưu'}
                    </Button>
                </DrawerFooter>
            }
        >
            <Spin spinning={isFetchingCounter} description="Đang tải dữ liệu...">
                <div className="min-h-[300px]">
                    {(mode === 'create' || counter) && <CounterForm form={form} mode={mode} showUnitColumn={showUnitColumn} isOpen={isOpen} counterId={mode === 'update' ? counterId : null} defaultUnitId={currentUser?.unitId} onSubmit={handleSubmit} />}
                </div>
            </Spin>
        </Drawer>
    )
}
